/**
 * Heal an expiring signed Supabase Storage URL into its permanent public form.
 *
 * The `uploads` bucket is PUBLIC, but earlier code persisted 1-hour signed URLs
 * (.../storage/v1/object/sign/uploads/<path>?token=...) straight into DB columns
 * (allowed_users.photo_url, settings.profile.logoUrl, receipts, …). Once the TTL
 * elapsed, every render 400'd and the image "disappeared". This rewrites the
 * signed form to the equivalent public URL
 * (.../storage/v1/object/public/uploads/<path>) on READ, so stored rows render
 * forever WITHOUT any DB write — applied at the data-mapping boundary.
 *
 * Pure + total: any other input — an already-public URL, an external URL, an
 * empty string, null, or undefined — passes through unchanged.
 */
const SIGNED_OBJECT_MARKER = '/storage/v1/object/sign/';
const PUBLIC_OBJECT_MARKER = '/storage/v1/object/public/';

export function normalizeStoredUploadUrl(url: string): string;
export function normalizeStoredUploadUrl(url: string | null | undefined): string | null | undefined;
export function normalizeStoredUploadUrl(url: string | null | undefined): string | null | undefined {
    if (!url || typeof url !== 'string') return url;
    const markerIdx = url.indexOf(SIGNED_OBJECT_MARKER);
    if (markerIdx === -1) return url; // already-public / external / any non-signed URL
    // Everything after the marker is `<bucket>/<path>` followed by the
    // `?token=…` query (and possibly a #fragment); the public URL needs neither.
    const objectPath = url
        .slice(markerIdx + SIGNED_OBJECT_MARKER.length)
        .split('?')[0]
        .split('#')[0];
    return url.slice(0, markerIdx) + PUBLIC_OBJECT_MARKER + objectPath;
}
